/**
 * `rigs.json` parsing, without Pixi (docs/art/ASSET_LIST.md "Rig"): each runner is a cutout rig
 * (head with swappable faces, torso, one arm and one leg image used for both sides, optional
 * tail), and costume items add images on top of those pieces. Points are in the piece image's
 * own pixels (the delivered @2x file).
 */

export type Point = [number, number]

/** Face expressions (ART_DIRECTION C3), left to right in `<name>_faces.png`. */
export const FACES = ['calm', 'determined', 'surprised', 'dizzy', 'overjoyed'] as const
export type Face = (typeof FACES)[number]

export interface RigPiece {
  src: string
  /** The joint the piece turns around (neck, shoulder, hip, tail root). */
  pivot: Point
}

export interface RigDef {
  /** Image pixels per world px (2 for @2x files). */
  px: number
  /** Head images by expression; all share `headPivot`. `calm` is required. */
  faces: Partial<Record<Face, string>> & { calm: string }
  headPivot: Point
  /** Points on the head image where hat and face items sit. */
  crown: Point
  eyes: Point
  /** The torso's pivot is the hip, where both legs attach. */
  torso: RigPiece & { neck: Point; shoulder: Point; back: Point; tailRoot?: Point }
  arm: RigPiece
  /** The sole is the bottom edge of the leg image. */
  leg: RigPiece
  tail?: RigPiece
}

/** Costume slots (ART_DIRECTION §2a). */
export const SLOTS = ['back', 'bottoms', 'shoes', 'top', 'face', 'hat'] as const
export type Slot = (typeof SLOTS)[number]
export type PieceName = 'head' | 'torso' | 'arm' | 'leg' | 'tail'

/**
 * One costume image on a piece. `pivot` is the point placed on the piece's pivot, or on the
 * head's `crown` / `eyes` / the torso's `back` point when `at` names one.
 */
export interface CostumePiece {
  src: string
  pivot: Point
  at?: 'crown' | 'eyes' | 'back'
}

export interface CostumeDef {
  slot: Slot
  pieces: Partial<Record<PieceName, CostumePiece>>
}

/**
 * A ride (`spriteManifest.json` kind `ride_<id>`): where the runner's hip sits, in the ride
 * image's own pixels (the @2x file), as for rig points.
 */
export interface RideDef {
  seat: Point
}

export interface Rigs {
  runners: Map<string, RigDef>
  costumes: Map<string, CostumeDef>
  rides: Map<string, RideDef>
}

type Obj = Record<string, unknown>

function fail(where: string, why: string): never {
  throw new Error(`rigs.json: ${where}: ${why}`)
}

function obj(v: unknown, where: string): Obj {
  if (typeof v !== 'object' || v === null || Array.isArray(v)) fail(where, 'not an object')
  return v as Obj
}

function point(v: unknown, where: string): Point {
  if (!Array.isArray(v) || v.length !== 2 || !v.every((n) => typeof n === 'number')) {
    fail(where, 'must be [x, y]')
  }
  return [v[0] as number, v[1] as number]
}

function str(v: unknown, where: string): string {
  if (typeof v !== 'string' || v === '') fail(where, 'must be a path')
  return v
}

function piece(v: unknown, where: string): RigPiece {
  const o = obj(v, where)
  return { src: str(o.src, `${where}.src`), pivot: point(o.pivot, `${where}.pivot`) }
}

function parseRig(v: unknown, where: string): RigDef {
  const o = obj(v, where)
  const px = o.px ?? 2
  if (typeof px !== 'number' || px <= 0) fail(`${where}.px`, 'must be > 0')
  const facesObj = obj(o.faces, `${where}.faces`)
  const faces: Partial<Record<Face, string>> = {}
  for (const [k, src] of Object.entries(facesObj)) {
    if (!(FACES as readonly string[]).includes(k)) fail(`${where}.faces`, `unknown face "${k}"`)
    faces[k as Face] = str(src, `${where}.faces.${k}`)
  }
  if (!faces.calm) fail(`${where}.faces`, 'needs "calm"')
  const t = obj(o.torso, `${where}.torso`)
  return {
    px,
    faces: faces as RigDef['faces'],
    headPivot: point(o.headPivot, `${where}.headPivot`),
    crown: point(o.crown, `${where}.crown`),
    eyes: point(o.eyes, `${where}.eyes`),
    torso: {
      ...piece(t, `${where}.torso`),
      neck: point(t.neck, `${where}.torso.neck`),
      shoulder: point(t.shoulder, `${where}.torso.shoulder`),
      back: point(t.back, `${where}.torso.back`),
      ...(t.tailRoot !== undefined
        ? { tailRoot: point(t.tailRoot, `${where}.torso.tailRoot`) }
        : {}),
    },
    arm: piece(o.arm, `${where}.arm`),
    leg: piece(o.leg, `${where}.leg`),
    ...(o.tail !== undefined ? { tail: piece(o.tail, `${where}.tail`) } : {}),
  }
}

const PIECES: readonly string[] = ['head', 'torso', 'arm', 'leg', 'tail']
const ATS: readonly string[] = ['crown', 'eyes', 'back']

function parseCostume(v: unknown, where: string): CostumeDef {
  const o = obj(v, where)
  if (typeof o.slot !== 'string' || !(SLOTS as readonly string[]).includes(o.slot)) {
    fail(`${where}.slot`, `must be one of ${SLOTS.join(', ')}`)
  }
  const pieces: CostumeDef['pieces'] = {}
  for (const [name, pv] of Object.entries(obj(o.pieces, `${where}.pieces`))) {
    if (!PIECES.includes(name)) fail(`${where}.pieces`, `unknown piece "${name}"`)
    const p = obj(pv, `${where}.pieces.${name}`)
    if (p.at !== undefined && (typeof p.at !== 'string' || !ATS.includes(p.at))) {
      fail(`${where}.pieces.${name}.at`, 'must be crown, eyes or back')
    }
    const base = piece(p, `${where}.pieces.${name}`)
    pieces[name as PieceName] =
      p.at === undefined ? base : { ...base, at: p.at as 'crown' | 'eyes' | 'back' }
  }
  return { slot: o.slot as Slot, pieces }
}

export function parseRigs(json: unknown): Rigs {
  const o = obj(json, 'root')
  const runners = new Map<string, RigDef>()
  for (const [k, v] of Object.entries(obj(o.runners ?? {}, 'runners'))) {
    runners.set(k, parseRig(v, `runners.${k}`))
  }
  const costumes = new Map<string, CostumeDef>()
  for (const [k, v] of Object.entries(obj(o.costumes ?? {}, 'costumes'))) {
    costumes.set(k, parseCostume(v, `costumes.${k}`))
  }
  const rides = new Map<string, RideDef>()
  for (const [k, v] of Object.entries(obj(o.rides ?? {}, 'rides'))) {
    rides.set(k, { seat: point(obj(v, `rides.${k}`).seat, `rides.${k}.seat`) })
  }
  return { runners, costumes, rides }
}

/** Every image path a rig or costume names (for loading). */
export function rigSources(rigs: Rigs): string[] {
  const out = new Set<string>()
  for (const r of rigs.runners.values()) {
    for (const src of Object.values(r.faces)) out.add(src)
    for (const p of [r.torso, r.arm, r.leg, r.tail]) if (p) out.add(p.src)
  }
  for (const c of rigs.costumes.values()) {
    for (const p of Object.values(c.pieces)) out.add(p.src)
  }
  return [...out]
}
